import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { authFetch } from "@/lib/authFetch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  // Initialize user from localStorage if available
  const [user, setUserState] = useState(() => {
    const stored = localStorage.getItem('cfi_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('cfi_user');
  });
  const location = useLocation();

  // Custom setUser that updates both state and localStorage
  const setUser = useCallback((userData) => {
    setUserState(userData);
    setIsAuthenticated(!!userData);
    if (userData) {
      localStorage.setItem('cfi_user', JSON.stringify(userData));
    } else {
      localStorage.removeItem('cfi_user');
    }
  }, []);

  useEffect(() => {
    // Skip auth check if we're processing a session_id (Google OAuth callback)
    if (location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    // If user data was passed from navigation state (from login or OAuth callback)
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      return;
    }

    // Validate session with backend — even if localStorage has cached user data.
    // This prevents stale/expired sessions (e.g. after deployment) from being
    // treated as valid, which caused /login to redirect away before showing the form.
    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API}/auth/me`, {
          credentials: "include",
        });

        if (response.ok) {
          const userData = await response.json();
          // Preserve session_token from localStorage — /auth/me doesn't return it
          const stored = localStorage.getItem('cfi_user');
          if (stored) {
            try {
              const prev = JSON.parse(stored);
              if (prev?.session_token && !userData.session_token) {
                userData.session_token = prev.session_token;
              }
            } catch {}
          }
          setUser(userData);
        } else if (response.status === 401) {
          // Backend explicitly says not authenticated — clear stale state
          setUser(null);
        }
        // Any other status (5xx, proxy hiccup): keep the cached user, do not bounce
      } catch (error) {
        // Network error / blocked request — do NOT log the user out.
        // Keep whatever is cached in localStorage so a transient failure
        // (blocked fetch, blocked 3rd-party cookie, flaky network) never
        // bounces an authenticated user back to the login page.
        console.error("Auth check error (keeping cached session):", error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [location.state, location.hash]); // eslint-disable-line react-hooks/exhaustive-deps

  // XMLHttpRequest fallback for login - bypasses fetch() interception by security software
  const xhrLogin = (url, payload) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.withCredentials = true;
      xhr.timeout = 15000;
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            reject(new Error(data.detail || "Login failed"));
          }
        } catch {
          reject(new Error("Login failed - invalid response"));
        }
      };
      xhr.onerror = () => reject(new Error("Network error - please check your internet connection and disable any antivirus web protection"));
      xhr.ontimeout = () => reject(new Error("Login timed out - please try again"));
      xhr.send(JSON.stringify(payload));
    });
  };

  const login = async (email, password) => {
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();
    const payload = { email: cleanEmail, password: cleanPassword };
    const url = `${API}/auth/login`;

    // Try fetch first, fall back to XMLHttpRequest if it fails
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Login failed");
      }

      const userData = await response.json();
      setUser(userData);
      return userData;
    } catch (fetchError) {
      // If fetch was blocked/failed (not a server error), try XMLHttpRequest
      if (fetchError.message && (
        fetchError.message.includes("Failed to fetch") ||
        fetchError.message.includes("NetworkError") ||
        fetchError.message.includes("network") ||
        fetchError.message.includes("blocked") ||
        fetchError.message.includes("abort") ||
        fetchError.message.includes("Body is disturbed") ||
        fetchError.message.includes("postMessage") ||
        fetchError.message.includes("clone")
      )) {
        console.warn("fetch() blocked, retrying with XMLHttpRequest...");
        const userData = await xhrLogin(url, payload);
        setUser(userData);
        return userData;
      }
      throw fetchError;
    }
  };

  const logout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      localStorage.removeItem('cfi_user');
    }
  };

  const getGoogleAuthUrl = () => {
    const redirectUrl = window.location.origin + "/login";
    return `${API}/auth/google/login?redirect_url=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, login, logout, setUser, getGoogleAuthUrl }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
